import React, { useState, useEffect } from "react";
import {
    Box,
    Grid,
    Typography,
    Card,
    CardContent,
    CardMedia,
    Chip,
    Button,
    LinearProgress,
    TextField,
    InputAdornment,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Paper,
    alpha,
    CircularProgress,
    IconButton,
    Menu,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import {
    Search,
    BookOpen,
    Clock,
    Star,
    Users,
    Layout,
    Filter,
    ArrowRight,
    Plus,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import backgroundImage from "../../assets/Vector 17.svg";
import { CheckCircle2 } from "lucide-react";
import CourseCardAI from "../../../../Assets/Images/CourseCardAI.png";
import CourseCardMath from "../../../../Assets/Images/CourseCardMath.png";
import CourseCardWatercolor from "../../../../Assets/Images/CourseCardWatercolor.png";
import CourseCardPoly from "../../../../Assets/Images/CourseCardPoly.png";
import CourseCardAesthetic from "../../../../Assets/Images/CourseCardAesthetic.png";
import Herocard from "../../../../Assets/Images/hero_tutor-remove.png"
import { FileText } from "lucide-react";
import CourseCardComponent from "../../Components/CourseCard/CourseCard";


import { useAppDispatch, useAppSelector } from "../../../../Redux/Store/Hooks";
import { getTutorCourses } from "../../../../Redux/Slices/ScheduledClasses/TutorCoursesSlice";


const TutoringCourses = () => {
    const [filter, setFilter] = useState("all");
    const [search, setSearch] = useState("");
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const { data: listData, isLoading } = useAppSelector((state) => state.TutorCoursesSlice);

    useEffect(() => {
        dispatch(getTutorCourses());
    }, [dispatch]);

    const courses = Array.isArray(listData?.data)
        ? listData.data.map((course: any) => ({
            ...course,
            title: course.full_name,
            category: course.course_category?.name || "Competitive Exams",
            lessons: course.subjects?.length || 0,
            duration: course.duration || "N/A",
            rating: 4.8, 
            students: course.students || 0,
            progress: 0,
            image: course.image || CourseCardPoly,
            status: course.status ? "Active" : "Inactive",
        }))
        : [];

    // Extract unique categories from courses
    const categories = Array.isArray(listData?.data)
        ? Array.from(new Set(listData.data.map((course: any) => course.course_category?.name).filter(Boolean)))
        : [];

    const filteredCourses = courses.filter(course => {
        const matchesSearch = (course.title || "").toLowerCase().includes(search.toLowerCase());
        const statusString = String(course.status || "");
        const matchesFilter = filter === "all" || 
                             (filter === "active" && statusString.toLowerCase().replace(" ", "") === "active") ||
                             (filter === "inactive" && statusString.toLowerCase().replace(" ", "") === "inactive") ||
                             course.category === filter;
        return matchesSearch && matchesFilter;
    });

    const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleFilterClose = () => {
        setAnchorEl(null);
    };

    const handleFilterSelect = (selectedFilter: string) => {
        setFilter(selectedFilter);
        handleFilterClose();
    };

    return (
        <Box sx={{ bgcolor: "#f8fafc", minHeight: "100vh" }}>
            {/* Header Hero Area */}
            <Paper
                elevation={0}
                sx={{
                    p: { xs: 2.5, md: 3 },
                    borderRadius: "16px",
                    position: "relative",
                    overflow: "hidden",
                    mb: 4,
                    height: { md: 190 },
                    display: "flex",
                    alignItems: "center",
                    background: "linear-gradient(90deg, #017CC9, #009DFF)",
                    color: "#fff",
                    boxShadow: "0 12px 30px rgba(0, 114, 255, 0.2), 0 4px 10px rgba(0, 0, 0, 0.05)",
                }}
            >
                {/* Background Vector Shape */}
                <Box
                    sx={{
                        position: "absolute",
                        right: -40,
                        bottom: -30,
                        width: "50%",
                        height: "100%",
                        opacity: 0.1,
                        zIndex: 0,
                        pointerEvents: "none",
                        "& img": {
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                            transform: "rotate(-5deg) scale(1.2)"
                        }
                    }}
                >
                    <img src={backgroundImage} alt="" />
                </Box>

                <Box sx={{ position: "relative", zIndex: 1, width: "100%" }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={3} sx={{ display: { xs: "none", md: "block" } }}>
                            <Box sx={{ position: "relative", height: 160, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 0.8, ease: "easeOut" }}
                                    style={{ width: "100%", maxWidth: 220 }}
                                >
                                    <img
                                        src={Herocard}
                                        alt="Hero Tutor"
                                        style={{ width: "100%", height: "auto", objectFit: "contain" }}
                                    />
                                </motion.div>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={9}>
                            <Typography variant="h4" sx={{ fontWeight: 900, mb: 1, letterSpacing: "-1px", textShadow: "0 1px 2px rgba(0,0,0,0.1)" }}>
                                My Course Library
                            </Typography>
                            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.9)", mb: 2, maxWidth: "600px", fontSize: "0.95rem", fontWeight: 400, lineHeight: 1.5 }}>
                                Build your skills with premium tutoring. Track your progress, manage your courses, and achieve your academic goals with ease.
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>
            </Paper>



            {/* Stats Quickbar
            <Box sx={{ mb: 5, display: "flex", gap: 3, flexWrap: "wrap", justifyContent: "center" }}>
                {[
                    { label: "Active Courses", value: "2", color: "#3b82f6", icon: <Layout size={18} /> },
                    { label: "Completed", value: "1", color: "#10b981", icon: <CheckCircle2 size={18} /> },
                    { label: "Total Students", value: "1,205", color: "#8b5cf6", icon: <Users size={18} /> },
                ].map((stat, idx) => (
                    <Box key={idx} sx={{
                        px: 4, py: 2,
                        borderRadius: "20px",
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        bgcolor: "#fff",
                        border: "1px solid rgba(0,0,0,0.05)",
                        boxShadow: "0 10px 25px rgba(0,0,0,0.03)",
                        transition: "all 0.3s",
                        "&:hover": { transform: "translateY(-4px)", boxShadow: "0 15px 35px rgba(0,0,0,0.06)" }
                    }}>
                        <Box sx={{ 
                            width: 42, 
                            height: 42, 
                            borderRadius: "12px", 
                            bgcolor: alpha(stat.color, 0.1), 
                            color: stat.color,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                        }}>
                            {stat.icon}
                        </Box>
                        <Box>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: "#64748b", display: "block", mb: 0.2 }}>{stat.label}</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: "#1e293b", lineHeight: 1 }}>{stat.value}</Typography>
                        </Box>
                    </Box>
                ))}
            </Box> 

            {/* Body Title & Search */}
            <Box sx={{ mb: 4, display: "flex", flexDirection: { xs: "column", md: "row" }, justifyContent: "space-between", alignItems: { md: "center" }, gap: 3 }}>
                <Box sx={{ display: "flex", flexGrow: 1, justifyContent: "flex-end", gap: 2 }}>
                    <TextField
                        placeholder="Search Courses"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        size="small"
                        sx={{
                            maxWidth: 300,
                            flexGrow: 1,
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "12px",
                                bgcolor: "#fff",
                                "& fieldset": { borderColor: "#e2e8f0" }
                            }
                        }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><Search size={18} color="#94a3b8" /></InputAdornment>
                        }}
                    />
                    <IconButton
                        onClick={handleFilterClick}
                        sx={{
                            borderRadius: "12px",
                            bgcolor: "#fff",
                            border: "1px solid #e2e8f0",
                            width: 40,
                            height: 40,
                            "&:hover": {
                                bgcolor: "#f8fafc",
                                borderColor: "#cbd5e1"
                            }
                        }}
                    >
                        <FilterListIcon sx={{ fontSize: 18, color: "#64748b" }} />
                    </IconButton>
                    <Menu
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleFilterClose}
                        PaperProps={{
                            sx: {
                                borderRadius: "12px",
                                mt: 1,
                                minWidth: 200,
                                boxShadow: "0 10px 25px rgba(0,0,0,0.1)"
                            }
                        }}
                    >
                        <MenuItem 
                            onClick={() => handleFilterSelect("all")}
                            sx={{ 
                                borderRadius: "8px", 
                                mx: 1, 
                                my: 0.5,
                                bgcolor: filter === "all" ? "#f1f5f9" : "transparent"
                            }}
                        >
                            All Courses
                        </MenuItem>
                        <MenuItem 
                            onClick={() => handleFilterSelect("active")}
                            sx={{ 
                                borderRadius: "8px", 
                                mx: 1, 
                                my: 0.5,
                                bgcolor: filter === "active" ? "#f1f5f9" : "transparent"
                            }}
                        >
                            Active
                        </MenuItem>
                        <MenuItem 
                            onClick={() => handleFilterSelect("inactive")}
                            sx={{ 
                                borderRadius: "8px", 
                                mx: 1, 
                                my: 0.5,
                                bgcolor: filter === "inactive" ? "#f1f5f9" : "transparent"
                            }}
                        >
                            Inactive
                        </MenuItem>
                        {categories.length > 0 && (
                            <>
                                <Box sx={{ px: 2, py: 1, mt: 1 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 600, color: "#64748b" }}>
                                        CATEGORIES
                                    </Typography>
                                </Box>
                                {categories.map((category) => (
                                    <MenuItem 
                                        key={category}
                                        onClick={() => handleFilterSelect(category)}
                                        sx={{ 
                                            borderRadius: "8px", 
                                            mx: 1, 
                                            my: 0.5,
                                            bgcolor: filter === category ? "#f1f5f9" : "transparent"
                                        }}
                                    >
                                        {category}
                                    </MenuItem>
                                ))}
                            </>
                        )}
                    </Menu>
                </Box>
            </Box>

            {/* Courses Grid */}
            {isLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "200px" }}>
                    {/* <CircularProgress /> */}
                </Box>
            ) : (
                <Grid container spacing={3}>
                    {filteredCourses.length > 0 ? (
                        filteredCourses.map((course: any) => (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={course.id}>
                                <CourseCardComponent
                                    title={course.title}
                                    image={course.image || CourseCardPoly}
                                    description={course.description}
                                    category={course.category}
                                    subjects={course.lessons}
                                    duration={`${course.duration} days`}
                                    status={course.status}
                                    onClick={() => navigate("/online-tutoring/courses/detail", { state: { course } })}
                                    actionLabel="View Course"
                                />
                            </Grid>
                    ))
                    ) : (
                        <Grid item xs={12}>
                            <Typography variant="body1" sx={{ textAlign: 'center', mt: 4, color: '#64748b' }}>
                                No courses found.
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            )}
        </Box>
    );
};

export default TutoringCourses;


